import { useRef } from 'react';
import { useChatStore } from '@client/store';
import { Message } from '@client/components';
import { v4 as uuidv4 } from 'uuid';

export const useMemoryOptimizer = () => {
  const setMessages = useChatStore((state) => state.setMessages);
  const lastMessageIds = useRef<string>('');

  const optimize = () => {
    const { messages } = useChatStore.getState();
    const currentIds = messages.map((m) => m.id).join(',');
    if (lastMessageIds.current === currentIds) return;
    lastMessageIds.current = currentIds;

    const optimized = messages.map((msg: Message) => {
      const { chunks, alternative_messages, ...rest } = msg;
      return {
        ...rest,
        chunks: chunks && chunks.length > 20 ? chunks.slice(-20) : chunks,
        alternative_messages:
          alternative_messages && alternative_messages.length > 2
            ? alternative_messages.slice(-2)
            : alternative_messages,
      };
    });

    setMessages(optimized);
  };

  const emergencyCleanup = () => {
    const { messages } = useChatStore.getState();
    const currentIds = messages.map((m) => m.id).join(',');
    if (lastMessageIds.current === currentIds) return;
    lastMessageIds.current = currentIds;

    const emergencyOptimized = messages.map((msg: Message) => {
      const { chunks, alternative_messages, meta, ...rest } = msg;
      return {
        ...rest,
        chunks: chunks ? chunks.slice(-5) : [],
        alternative_messages: alternative_messages
          ? alternative_messages.slice(-1)
          : [],
      };
    });

    setMessages(emergencyOptimized);
  };

  const clearTraces = () => {
    lastMessageIds.current = '';
  };

  return { optimize, emergencyCleanup, clearTraces };
};

export const memoryOptimizer = (() => {
  let _lastIds = '';

  const optimize = () => {
    const { messages, setMessages } = useChatStore.getState();
    const currentIds = messages.map((m) => m.id).join(',');
    if (_lastIds === currentIds) return;
    _lastIds = currentIds;

    const optimized = messages.map((msg: Message) => {
      const { chunks, alternative_messages, ...rest } = msg;
      return {
        ...rest,
        chunks: chunks && chunks.length > 20 ? chunks.slice(-20) : chunks,
        alternative_messages:
          alternative_messages && alternative_messages.length > 2
            ? alternative_messages.slice(-2)
            : alternative_messages,
      };
    });

    setMessages(optimized);
  };

  const emergencyCleanup = () => {
    const { messages, setMessages } = useChatStore.getState();
    const currentIds = messages.map((m) => m.id).join(',');
    if (_lastIds === currentIds) return;
    _lastIds = currentIds;

    const emergencyOptimized = messages.map((msg: Message) => {
      const { chunks, alternative_messages, meta, ...rest } = msg;
      return {
        ...rest,
        chunks: chunks ? chunks.slice(-5) : [],
        alternative_messages: alternative_messages
          ? alternative_messages.slice(-1)
          : [],
      };
    });

    setMessages(emergencyOptimized);
  };

  const clearTraces = () => {
    _lastIds = '';
  };

  const offloadHeavyTask = (
    task: () => void,
    timeout = 5000,
    componentName = 'UnknownComponent'
  ) => {
    if (typeof Worker !== 'undefined') {
      const wrappedTask = `
        onmessage = async () => {
          const id = '${uuidv4()}-${componentName}';
          let finished = false;
          const timer = setTimeout(() => { 
            if(!finished) close(); 
          }, ${timeout});

          try {
            (${task.toString()})();
          } finally {
            finished = true;
            clearTimeout(timer);
            postMessage({ id, status: 'done' });
            close();
          }
        }
      `;
      const blob = new Blob([wrappedTask], { type: 'application/javascript' });
      const worker = new Worker(URL.createObjectURL(blob));
      worker.postMessage(null);
      return new Promise((resolve) => {
        worker.onmessage = (e) => {
          resolve(e.data);
          worker.terminate();
        };
      });
    } else {
      const timer = setTimeout(
        () => console.warn('Task killed due to timeout'),
        timeout
      );
      try {
        task();
      } finally {
        clearTimeout(timer);
      }
    }
  };

  return { optimize, emergencyCleanup, clearTraces, offloadHeavyTask };
})();
